import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Plus, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter } from "@/components/ui/drawer";

export default function Properties() {
  const nav = useNavigate();
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", service_address: "", city: "", state: "", zip: "", access_instructions: "" });

  const load = () => api.get(`/properties${q ? `?q=${encodeURIComponent(q)}` : ""}`).then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.service_address) { toast.error("Service address required"); return; }
    await api.post("/properties", form);
    setOpen(false); setForm({ name: "", service_address: "", city: "", state: "", zip: "", access_instructions: "" });
    toast.success("Property created"); load();
  };

  return (
    <div className="min-h-screen">
      <header className="px-5 pt-7 pb-3 flex items-center justify-between">
        <h1 className="font-heading text-3xl font-black tracking-tight">Properties</h1>
        <Button data-testid="new-property-page-btn" size="sm" className="h-10 gap-1.5 bg-orange-600 hover:bg-orange-700 font-bold" onClick={() => setOpen(true)}><Plus size={18} weight="bold" /> Add</Button>
      </header>
      <div className="px-5 space-y-3">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="relative">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input data-testid="properties-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search address or nickname" className="h-11 pl-10" />
        </form>
        {list.length === 0 && <div className="text-sm text-zinc-400 py-10 text-center border border-dashed border-zinc-200 rounded-xl" data-testid="properties-empty">No properties yet.</div>}
        <div className="space-y-2.5 pb-4">
          {list.map((p) => (
            <button key={p.id} data-testid={`property-row-${p.id}`} onClick={() => nav(`/property/${p.id}`)}
              className="w-full text-left bg-white border border-zinc-200 rounded-xl p-3.5 flex items-center gap-3 active:scale-[0.99] transition-transform">
              <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0"><MapPin size={22} weight="bold" /></div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-zinc-900 truncate">{p.name || p.service_address}</p>
                <p className="text-xs text-zinc-500 truncate">{[p.service_address, p.city, p.state, p.zip].filter(Boolean).join(", ")}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent className="max-w-md mx-auto">
          <DrawerHeader><DrawerTitle className="font-heading">New Property</DrawerTitle></DrawerHeader>
          <div className="px-4 pb-2 space-y-2">
            <Input placeholder="Nickname" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-10" data-testid="prop-name" />
            <Input placeholder="Service address" value={form.service_address} onChange={(e) => setForm({ ...form, service_address: e.target.value })} className="h-10" data-testid="prop-address" />
            <div className="grid grid-cols-3 gap-2">
              <Input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="h-10" />
              <Input placeholder="State" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="h-10" />
              <Input placeholder="ZIP" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} className="h-10" />
            </div>
            <Input placeholder="Access / gate code notes" value={form.access_instructions} onChange={(e) => setForm({ ...form, access_instructions: e.target.value })} className="h-10" />
          </div>
          <DrawerFooter><Button data-testid="create-property-btn" onClick={create} className="h-12 font-bold bg-orange-600 hover:bg-orange-700">Create Property</Button></DrawerFooter>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
